import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import {
  Globe,
  ChevronRight,
  Loader2,
  Volume2,
  Languages
} from 'lucide-react';
import api from '../lib/api';

const LANGUAGE_COLORS = {
  de: 'from-yellow-400 to-red-500',
  it: 'from-green-400 to-red-500',
  pl: 'from-red-400 to-white',
  ro: 'from-blue-400 to-yellow-500',
  el: 'from-blue-400 to-white',
};

export default function PhrasesPage() {
  const { t } = useLanguage();
  const [languages, setLanguages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchLanguages();
  }, []);

  const fetchLanguages = async () => {
    try {
      const response = await api.get('/phrases');
      setLanguages(response.data);
    } catch (error) {
      console.error('Failed to load languages:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-100 pt-20" data-testid="phrases-page">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-signal-blue/10 rounded-2xl mb-6">
            <Languages className="w-8 h-8 text-signal-blue" />
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-slate-800 mb-4">
            {t('phrases.title', 'Essential Phrases for Daily Life in Europe')}
          </h1>
          <p className="text-slate-600 text-lg max-w-2xl mx-auto">
            {t('phrases.subtitle', 'Moving, working, or traveling in Europe? These essential phrases help you navigate daily life — from greetings and meetings to ordering food and getting around town.')}
          </p>
        </div>

        {/* Language Cards */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 text-signal-blue animate-spin" />
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {languages.map((lang) => (
              <Link
                key={lang.code}
                to={`/phrases/${lang.code}`}
                className="group bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden hover:shadow-lg hover:border-slate-300 transition-all"
                data-testid={`language-card-${lang.code}`}
              >
                {/* Flag Banner */}
                <div className={`h-3 bg-gradient-to-r ${LANGUAGE_COLORS[lang.code] || 'from-slate-400 to-slate-500'}`} />

                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <span className="text-4xl mb-2 block">{lang.flag}</span>
                      <h3 className="font-heading text-xl font-bold text-slate-800 group-hover:text-signal-blue transition-colors">
                        {t(`lang.${lang.code}`, lang.name)}
                      </h3>
                    </div>
                    <ChevronRight className="w-5 h-5 text-slate-400 group-hover:text-signal-blue group-hover:translate-x-1 transition-all" />
                  </div>

                  <div className="flex items-center gap-4 text-sm text-slate-500">
                    <span className="flex items-center gap-1">
                      <Globe className="w-4 h-4" />
                      {lang.phrase_count} {t('phrases.phrases_label', 'phrases')}
                    </span>
                    <span className="flex items-center gap-1">
                      <Volume2 className="w-4 h-4" />
                      {t('phrases.audio', 'Audio')}
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}

        {/* Bottom Info */}
        <div className="mt-12 text-center">
          <p className="text-slate-500 text-sm">
            {t('phrases.tagline', 'Simple. Practical. Human. — Built to help you feel confident from day one.')}
          </p>
        </div>
      </div>
    </div>
  );
}
